import { useMutation } from '@apollo/client';
import React from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Text,
  useToast,
} from '@eluve/components';
import { useTenantBundles } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { upsertDefaultTemplateBundleIdMutation } from './operations';
import { usePromptTemplates } from './usePromptTemplates';

// Bundles are enabled for all tenants (ENG-4406): user template preferences
// are always the default-bundle selector. The legacy prompt/output template
// selectors and the USE_MASTRA_FOR_APPOINTMENT_SUMMARY branch were removed.
export const PromptTemplateSettings: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('user_settings');

  const { userSettingsDefaultBundleId } = usePromptTemplates();

  const { bundles, loading: bundlesLoading } = useTenantBundles(tenantId);

  const [upsertDefaultBundleId] = useMutation(
    upsertDefaultTemplateBundleIdMutation,
    {
      onError: () => toast.error(t('userSettings.templates.updateFailed')),
      optimisticResponse: (data) => ({
        updateTenantUserSettingsByPk: {
          __typename: 'TenantUserSettings' as const,
          tenantId,
          userId,
          defaultTemplateBundleId: data.defaultTemplateBundleId ?? null,
        },
      }),
      onCompleted: () => {
        analytics.capture('preferred_template_bundle_update', {
          tenantId,
          userId,
        });
      },
    },
  );

  const selectedBundle = bundles.find(
    (b) => b.id === userSettingsDefaultBundleId,
  );

  return (
    <div className={className}>
      <Select
        value={userSettingsDefaultBundleId ?? ''}
        onValueChange={(bundleId) => {
          upsertDefaultBundleId({
            variables: {
              tenantId,
              userId,
              defaultTemplateBundleId: bundleId,
            },
          });
        }}
      >
        <SelectTrigger className="text-text-primary">
          {bundlesLoading
            ? t('userSettings.templates.loading')
            : (selectedBundle?.displayName ??
              t('userSettings.templates.select'))}
        </SelectTrigger>
        <SelectContent>
          {bundles.map((bundle) => (
            <SelectItem
              key={bundle.id}
              className="focus:bg-bg-accent-quaternary"
              value={bundle.id}
            >
              <div className="max-w-96 py-1">
                <Text>{bundle.displayName}</Text>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
